import React, { memo, useCallback, useRef } from 'react';
import DraggableList from 'react-draggable-list';
import Animated from 'react-native-reanimated';
import { View, Pressable, StyleSheet } from 'react-native';
import { AppView, CardView, rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, Spacer, TextButton, withAnimatedNavigationHeader, } from 'design-system';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { IconBoldDrag } from 'design-system/icons/IconBoldDrag';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import useColors from 'hooks/useColors';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { IconBoldSubtract } from 'design-system/icons/IconBoldSubtract';
import IconCircle from 'design-system/icons/IconCircle';
import Text from 'design-system/Text';
import RemovedItem from 'features/settings/organize/components/RemovedItem';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import IconTransactionCategory from '../icons/IconTransactionCategory';
import { useReorderCategories } from '../hooks/useReorderCategories';
const DraggableItem = memo(({ category, onRemoveItem, itemSelected, dragHandleProps, }) => {
    const colors = useColors();
    const draggableStyles = useStyles(draggableStyleSet);
    const scale = itemSelected * 0.05 + 1;
    return (<Pressable {...dragHandleProps}>
        <View style={[
            draggableStyles.rowItem,
            styles.item,
            { transform: [{ scale }] },
        ]}>
          <TouchableBounce onPress={onRemoveItem} testID="OrganizeRemove">
            <IconBoldSubtract />
          </TouchableBounce>
          <Spacer w={16}/>
          <IconCircle width={44} height={44} color={colors.cards.onDark}>
            <IconTransactionCategory lightBackground category={category}/>
          </IconCircle>
          <Spacer w={16}/>
          <Text Text-16 flex>
            {category.displayName}
          </Text>
          <IconBoldDrag />
        </View>
      </Pressable>);
});
const draggableStyleSet = createStyleSheets((colors) => ({
    rowItem: {
        borderWidth: 1,
        overflow: 'hidden',
        padding: rem(16),
        flexDirection: 'row',
        borderRadius: rem(12),
        borderColor: colors.background.dark,
        backgroundColor: colors.background.dark,
    },
}));
const animationConfig = {
    dampingRatio: 3,
    duration: 300,
};
const keyExtractor = (item) => item.id;
const ReorderCategoriesScreen = withAnimatedNavigationHeader(({ scrollHandler }) => {
    const { visibleCategoriesOrder, hiddenCategories, handleDone, onRevertOrder, onRemoveItem, onAddItem, onMoveEnd, } = useReorderCategories();
    const renderItem = useCallback(({ item, itemSelected, dragHandleProps, }) => (<DraggableItem key={item.id} category={item} onRemoveItem={() => onRemoveItem(item)} itemSelected={itemSelected} dragHandleProps={dragHandleProps}/>), [onRemoveItem]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(true);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING + SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const containerRef = useRef(null);
    const container = useCallback(() => containerRef.current, []);
    const colors = useColors();
    return (<AppView withFrame={false}>
          <Animated.ScrollView ref={containerRef} onScroll={scrollHandler} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle}>
            <ScreenInfoHeader title="Reorder categories" subTitle="Drag to reorder your categories. You can also hide any categories that aren’t relevant to you"/>
            <CardView style={styles.card} noMarginTop>
              <DraggableList list={visibleCategoriesOrder} itemKey={keyExtractor} 
    // @ts-ignore
    template={renderItem} padding={0} springConfig={animationConfig} onMoveEnd={onMoveEnd} autoScrollRegionSize={200} container={container}/>
              <TextButton text="Revert to original (A to Z)" onPress={onRevertOrder} small color={{ OnBrand: true }} style={styles.revertButton}/>
            </CardView>
            {!!hiddenCategories?.length && (<CardView style={styles.card} noMarginTop>
                {hiddenCategories.map((data) => (<RemovedItem key={data.id} title={data.displayName} onAdd={() => onAddItem(data)} icon={<IconCircle width={44} height={44} color={colors.cards.onDark}>
                        <IconTransactionCategory lightBackground category={data}/>
                      </IconCircle>} style={styles.item}/>))}
              </CardView>)}
          </Animated.ScrollView>
          <Button title="Save" onPress={handleDone} floating brand disabled={!visibleCategoriesOrder.length}/>
        </AppView>);
}, ({ colors }) => ({
    hideTitleOnScroll: true,
    title: 'Reorder categories',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    card: {
        paddingVertical: rem(12),
    },
    item: {
        paddingVertical: rem(10),
        marginHorizontal: rem(16),
        marginVertical: rem(4),
        alignItems: 'center',
    },
    revertButton: {
        marginTop: rem(21.5),
        marginBottom: rem(13.5),
        alignSelf: 'center',
    },
});
export default ReorderCategoriesScreen;
